import _typeof from '@babel/runtime/helpers/typeof';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _PropTypes from 'prop-types';
import * as React from 'react';
import { useId } from 'react-id-generator';
import styled from 'styled-components';
import { KEYS } from '../../constants/keys.js';
import { useResponsiveChildren } from '../../hooks/use_responsive_children.js';
import { useTranslations } from '../../hooks/use_translations.js';
import { spacing8x } from '../../styles/spacing.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { ButtonMenu } from '../ButtonMenu/ButtonMenu.js';
import { IconMore } from '../Icons/IconMore.js';
import { ActionToolbarDivider } from './ActionToolbarDivider.js';
import { ActionToolbarItemWrapper } from './ActionToolbarItem.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["children", "aria-label"];
var ActionToolbarGroupMoreButton = styled.div.withConfig({
  displayName: "ActionToolbarGroup__ActionToolbarGroupMoreButton",
  componentId: "sc-zzrcvb-0"
})(["&&{display:inline-flex;align-items:center;width:0;}&& button{min-height:15px;padding:5px 0;margin-left:-10px;color:", ";}&& button:hover,&& button:focus{color:", ";background:", ";}"], function (props) {
  return props.theme.neutralColors.white;
}, function (props) {
  return props.theme.neutralColors.white;
}, function (props) {
  return props.theme.neutralColors.smoke;
});
var ActionToolbarGroupWrapper = styled.div.withConfig({
  displayName: "ActionToolbarGroup__ActionToolbarGroupWrapper",
  componentId: "sc-zzrcvb-1"
})(["&&{height:48px;margin:0 auto;padding:11px ", ";border-radius:", ";background:", ";color:", ";}&& ", ":last-of-type > *{margin-right:0;}"], spacing8x, spacing8x, function (props) {
  return props.theme.neutralColors.charcoal;
}, function (props) {
  return props.theme.neutralColors.white;
}, ActionToolbarItemWrapper);

var isDivider = function isDivider(child) {
  return typeof child !== "string" && typeof child !== "number" && (child === null || child === void 0 ? void 0 : child.type) === ActionToolbarDivider;
};

var findPriorities = function findPriorities(c) {
  var priorities = [];
  c.forEach(function (item, i) {
    var isPriorityItem = item.props.priority || !!item.props.as;
    if (isPriorityItem) priorities.push(i);
  });
  var priorityGroups = c.filter(function (item, i) {
    return priorities.includes(i);
  }).map(function (item) {
    return item.props.group;
  });
  c.forEach(function (item, i) {
    var isPriorityItem = isDivider(item) && priorityGroups.includes(item.props.group);
    if (isPriorityItem) priorities.push(i);
  });
  return priorities.sort(function (a, b) {
    return a - b;
  });
};

var ActionToolbarGroup = /*#__PURE__*/React.forwardRef(function ActionToolbarGroup2(props, ref) {
  var _useId = useId(1, "toolbarButton-"),
      _useId2 = _slicedToArray(_useId, 1),
      defaultLabel = _useId2[0];

  var _useThemeContext = useThemeContext(),
      localization = _useThemeContext.localization;

  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
      t = _useTranslations.t;

  var children = props.children,
      _props$ariaLabel = props["aria-label"],
      ariaLabel = _props$ariaLabel === void 0 ? defaultLabel : _props$ariaLabel,
      rest = _objectWithoutProperties(props, _excluded);

  var _React$useState = React.useState(0),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      focusedIndex = _React$useState2[0],
      setFocusedIndex = _React$useState2[1];

  var toolbarRef = React.useRef(null);
  var overflowMenuRef = React.useRef(null);
  var preservedIndex = React.useMemo(function () {
    return findPriorities(children);
  }, [children]);

  var _useResponsiveChildre = useResponsiveChildren({
    children: children,
    parentRef: toolbarRef,
    preservedIndex: preservedIndex,
    getRefProp: function getRefProp(child) {
      return !isDivider(child) ? "wrapperRef" : "ref";
    }
  }),
      _useResponsiveChildre2 = _slicedToArray(_useResponsiveChildre, 2),
      fittingChildren = _useResponsiveChildre2[0],
      overflowingChildren = _useResponsiveChildre2[1];

  var focusItems = fittingChildren.map(function () {
    return /*#__PURE__*/React.createRef();
  });
  if (overflowingChildren.length > 0 && overflowMenuRef.current) focusItems.push(overflowMenuRef);
  if (focusItems.length < focusedIndex) setFocusedIndex(0);

  var handleKeyNav = function handleKeyNav(e) {
    switch (e.key) {
      case KEYS.ARROW_RIGHT:
        {
          var _focusItems$nextIndex;

          e.preventDefault();
          var stepAmount = isDivider(fittingChildren[focusedIndex + 1]) ? 2 : 1;
          var nextIndex = Math.min(focusedIndex + stepAmount, focusItems.length - 1);
          (_focusItems$nextIndex = focusItems[nextIndex].current) === null || _focusItems$nextIndex === void 0 ? void 0 : _focusItems$nextIndex.focus();
          setFocusedIndex(nextIndex);
          break;
        }

      case KEYS.ARROW_LEFT:
        {
          var _focusItems$_nextInde;

          e.preventDefault();

          var _stepAmount = isDivider(fittingChildren[focusedIndex - 1]) ? 2 : 1;

          var _nextIndex = Math.max(focusedIndex - _stepAmount, 0);

          (_focusItems$_nextInde = focusItems[_nextIndex].current) === null || _focusItems$_nextInde === void 0 ? void 0 : _focusItems$_nextInde.focus();
          setFocusedIndex(_nextIndex);
          break;
        }

      case KEYS.END:
        {
          var _focusItems$current;

          e.preventDefault();
          (_focusItems$current = focusItems[focusItems.length - 1].current) === null || _focusItems$current === void 0 ? void 0 : _focusItems$current.focus();
          setFocusedIndex(focusItems.length - 1);
          break;
        }

      case KEYS.HOME:
        {
          var _focusItems$0$current;

          e.preventDefault();
          (_focusItems$0$current = focusItems[0].current) === null || _focusItems$0$current === void 0 ? void 0 : _focusItems$0$current.focus();
          setFocusedIndex(0);
          break;
        }
    }
  };

  var label = ariaLabel.toLowerCase().includes("toolbarButton") ? ariaLabel : "toolbarButton-".concat(ariaLabel);
  var buttonMenuChildren = overflowingChildren.filter(function (child) {
    return !isDivider(child);
  });
  return /*#__PURE__*/jsx(ActionToolbarGroupWrapper, _objectSpread(_objectSpread({
    ref: ref,
    "aria-label": label
  }, rest), {}, {
    children: /*#__PURE__*/jsxs("div", {
      style: {
        height: "100%"
      },
      ref: toolbarRef,
      children: [React.Children.map(fittingChildren, function (c, index) {
        return /*#__PURE__*/React.cloneElement(c, {
          onKeyDown: handleKeyNav,
          tabIndex: index === focusedIndex ? 0 : -1,
          ref: focusItems[index],
          onFocus: function onFocus() {
            return setFocusedIndex(index);
          },
          style: isDivider(c) && (index === 0 || index === fittingChildren.length - 1) ? _objectSpread(_objectSpread({}, c.props.style), {}, {
            display: "none"
          }) : c.props.style
        });
      }), overflowingChildren.length > 0 && /*#__PURE__*/jsx(ActionToolbarGroupMoreButton, {
        ref: ref,
        children: /*#__PURE__*/jsx(ButtonMenu, {
          buttonProps: {
            onKeyDown: handleKeyNav,
            onFocus: function onFocus() {
              return setFocusedIndex(focusItems.length - 1);
            },
            tabIndex: focusedIndex === focusItems.length - 1 ? 0 : -1,
            hideLabel: true,
            label: t("ActionToolbar.moreActions"),
            startAdornment: IconMore,
            variant: "ghost",
            ref: overflowMenuRef
          },
          options: buttonMenuChildren.map(function (child, i) {
            var _child$props = child.props,
                childLabel = _child$props.label,
                group = _child$props.group,
                onClick = _child$props.onClick;
            var nextChild = buttonMenuChildren[i + 1];
            var border = nextChild && nextChild.props.group !== group;
            return {
              children: childLabel,
              border: border,
              onClick: onClick
            };
          })
        })
      })]
    })
  }));
});
ActionToolbarGroup.propTypes = {
  "aria-label": _PropTypes.string,
  "children": _PropTypes.oneOfType([_PropTypes.arrayOf(_PropTypes.shape({
    "key": _PropTypes.oneOfType([_PropTypes.oneOf([null]), _PropTypes.number, _PropTypes.string]).isRequired,
    "props": _PropTypes.shape({
      "as": _PropTypes.oneOfType([_PropTypes.string, _PropTypes.func, _PropTypes.object]),
      "group": _PropTypes.number,
      "hideLabel": _PropTypes.bool,
      "label": _PropTypes.string,
      "onClick": _PropTypes.func,
      "priority": _PropTypes.bool,
      "startAdornment": function checkAdornment(props, propName, componentName) {
        var isReactNodeType = function isReactNodeType(el) {
          var REACT_ELEMENT_TYPE = typeof Symbol === 'function' && Symbol.for ? Symbol.for('react.element') : 0xeac7;
          var isValidElement = _typeof(el) === 'object' && el !== null && el['\$\$typeof'] === REACT_ELEMENT_TYPE;
          return el === undefined || el === null || typeof el === 'boolean' || typeof el === 'string' || isValidElement;
        };

        var adornment = (propName === 'startAdornment' || propName === 'endAdornment') && props[propName];

        if (!isReactNodeType(adornment)) {
          var name = adornment.displayName || adornment.name || 'Component';

          if (!(name.indexOf('Icon') === 0)) {
            return new Error("Invalid prop \"".concat(propName, "\". Invalid React component with name \"").concat(name, "\" was supplied to \"").concat(componentName, "\". Only Icon components are allowed.\n"));
          }
        }
      },
      "wrapperRef": _PropTypes.any
    }).isRequired,
    "type": _PropTypes.oneOfType([_PropTypes.func, _PropTypes.object, _PropTypes.string]).isRequired
  })), _PropTypes.object, _PropTypes.shape({
    "__@iterator@836": _PropTypes.func.isRequired,
    "__@unscopables@3076": _PropTypes.func.isRequired,
    "concat": _PropTypes.func.isRequired,
    "copyWithin": _PropTypes.func.isRequired,
    "entries": _PropTypes.func.isRequired,
    "every": _PropTypes.func.isRequired,
    "fill": _PropTypes.func.isRequired,
    "filter": _PropTypes.func.isRequired,
    "find": _PropTypes.func.isRequired,
    "findIndex": _PropTypes.func.isRequired,
    "flat": _PropTypes.func.isRequired,
    "flatMap": _PropTypes.func.isRequired,
    "forEach": _PropTypes.func.isRequired,
    "includes": _PropTypes.func.isRequired,
    "indexOf": _PropTypes.func.isRequired,
    "join": _PropTypes.func.isRequired,
    "keys": _PropTypes.func.isRequired,
    "lastIndexOf": _PropTypes.func.isRequired,
    "length": _PropTypes.number.isRequired,
    "map": _PropTypes.func.isRequired,
    "pop": _PropTypes.func.isRequired,
    "push": _PropTypes.func.isRequired,
    "reduce": _PropTypes.func.isRequired,
    "reduceRight": _PropTypes.func.isRequired,
    "reverse": _PropTypes.func.isRequired,
    "shift": _PropTypes.func.isRequired,
    "slice": _PropTypes.func.isRequired,
    "some": _PropTypes.func.isRequired,
    "sort": _PropTypes.func.isRequired,
    "splice": _PropTypes.func.isRequired,
    "toExponential": _PropTypes.func.isRequired,
    "toFixed": _PropTypes.func.isRequired,
    "toLocaleString": _PropTypes.func.isRequired,
    "toPrecision": _PropTypes.func.isRequired,
    "toString": _PropTypes.func.isRequired,
    "unshift": _PropTypes.func.isRequired,
    "valueOf": _PropTypes.func.isRequired,
    "values": _PropTypes.func.isRequired
  })]).isRequired
};
ActionToolbarGroup.displayName = "ActionToolbarGroup";

export { ActionToolbarGroup };
